import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import useApi from "@/hooks/useApi";
import { useCerberus } from "@/hooks/useCerberus";
import SessionService from "@/services/SessionService";
import { passwordRequirements, passwordSchema } from "@/common/passwordPolicy";

interface VerifyEmailStepProps {
  email: string;
  onResend: () => void;
  resending: boolean;
  resendConfirmation: string | null;
}

/**
 * Finish sign-up without leaving the page: the 6-digit code from the email proves the address, and
 * the password is set in the same step (2026-10-05). The password is only ever sent together with
 * the code — taking it before the address is proven would let someone pre-register another
 * person's email with a password they chose. The emailed link still works as a fallback.
 */
export default function VerifyEmailStep({ email, onResend, resending, resendConfirmation }: VerifyEmailStepProps) {
  const api = useApi(false);
  const navigate = useNavigate();
  const { refreshUserData } = useCerberus();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from the email.");
      return;
    }
    try {
      await passwordSchema().validate(password);
    } catch (validation) {
      setError((validation as Error).message);
      return;
    }
    if (password !== confirm) {
      setError("The passwords don't match.");
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post<{ success: boolean; error?: string; message?: string; expiresIn?: number }>(
        "/invitation/patient/verify",
        { email, code, password, confirmPassword: confirm },
      );
      if (data.success && data.expiresIn) {
        // The session cookie arrived with this response; record its expiry and go in.
        SessionService.getInstance().setSession(data.expiresIn);
        await refreshUserData();
        navigate("/", { replace: true });
        return;
      }
      setError(data.error ?? data.message ?? "We couldn't create your account. Please try again.");
    } catch {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit} noValidate sx={{ py: 2 }}>
      <Stack spacing={1} sx={{ alignItems: "center", textAlign: "center", mb: 3 }}>
        <MarkEmailReadOutlinedIcon color="primary" sx={{ fontSize: 48 }} aria-hidden />
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
          Check your email for a code
        </Typography>
        <Typography color="text.secondary">
          We sent a 6-digit code to <strong>{email}</strong>. Enter it below and choose a password to finish.
        </Typography>
      </Stack>

      <Stack spacing={2.5}>
        <TextField
          label="6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          autoComplete="one-time-code"
          slotProps={{
            htmlInput: {
              inputMode: "numeric",
              maxLength: 6,
              style: { letterSpacing: "0.5em", fontSize: "1.5rem", textAlign: "center", fontWeight: 600 },
              "aria-describedby": "code-help",
            },
          }}
          fullWidth
        />
        <Typography id="code-help" variant="caption" color="text.secondary" sx={{ mt: "4px !important" }}>
          It can take a minute to arrive — check your spam folder too. The code expires in 30 minutes.
        </Typography>

        <TextField
          label="Choose a password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          fullWidth
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          helperText={passwordRequirements.join(" · ")}
        />
        <TextField
          label="Confirm password"
          type={showPassword ? "text" : "password"}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          fullWidth
        />

        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={busy}
          sx={{ borderRadius: 999, py: 1.5, fontWeight: 700, textTransform: "none", fontSize: "1rem" }}
        >
          {busy ? "Creating your account…" : "Create account"}
        </Button>

        <Box sx={{ textAlign: "center" }}>
          {resendConfirmation ? (
            <Typography variant="body2" color="text.secondary">
              {resendConfirmation}
            </Typography>
          ) : (
            <Button variant="text" onClick={onResend} disabled={resending} sx={{ textTransform: "none" }}>
              {resending ? "Sending…" : "Didn't get it? Send a new code"}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            On another device? The link in the email works too.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
